import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg shadow-lg shadow-violet-500/25">
              📊
            </div>
            <span className="text-xl font-bold text-white">WatchTheSize</span>
          </Link>
          
          <div className="flex items-center gap-6 text-sm text-zinc-400">
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="text-white font-medium">Privacy</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          
          <p className="text-zinc-400 mb-12">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <div className="prose prose-invert prose-zinc max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                WatchTheSize ("we", "us", or "our") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your information when you use our disk monitoring service.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                By using WatchTheSize, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.1 Account Information</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Name (optional)</li>
                <li>Billing information (for paid plans)</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.2 Server Monitoring Data</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Our monitoring agent collects the following data from your servers:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Disk usage statistics (total capacity, used space, available space)</li>
                <li>Directory sizes and file counts</li>
                <li>File and directory names for top space consumers</li>
                <li>System information (hostname, operating system version)</li>
                <li>Agent version and health metrics</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                <strong className="text-white">Important:</strong> We do not read or collect the contents of your files. We only collect metadata about file sizes and names.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">2.3 Usage Data</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We automatically collect certain information when you use our service:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent</li>
                <li>Device information</li>
                <li>Referring website</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use the collected information for:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Providing and maintaining the Service</li>
                <li>Displaying disk usage statistics in your dashboard</li>
                <li>Sending alerts and notifications about disk usage changes</li>
                <li>Providing AI-powered insights and recommendations</li>
                <li>Processing payments and managing subscriptions</li>
                <li>Responding to customer support requests</li>
                <li>Improving and optimizing our Service</li>
                <li>Detecting and preventing fraud or abuse</li>
                <li>Complying with legal obligations</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">4. Data Storage and Security</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We take the security of your data seriously and implement appropriate measures:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>All data is encrypted in transit using TLS 1.3</li>
                <li>Data at rest is encrypted using AES-256</li>
                <li>Passwords are hashed using bcrypt</li>
                <li>Access to data is restricted to authorized personnel only</li>
                <li>Regular security audits and penetration testing</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                Your data is stored in secure data centers with industry-standard physical and network security measures.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Retention</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We retain your data for as long as necessary to provide the Service:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Account information: Until you delete your account</li>
                <li>Monitoring data: Based on your plan (7, 30, or 90 days)</li>
                <li>Billing records: As required by law (typically 7 years)</li>
                <li>Usage logs: 90 days</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                When you delete your account, we will delete or anonymize your personal data within 30 days, except where retention is required by law.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">6. Data Sharing</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We do not sell your personal data. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li><strong className="text-white">Service Providers:</strong> Third-party companies that help us operate our service (payment processors, email providers, cloud hosting)</li>
                <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                <li><strong className="text-white">With Your Consent:</strong> When you explicitly agree to share information</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li><strong className="text-white">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-white">Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong className="text-white">Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong className="text-white">Portability:</strong> Request your data in a machine-readable format</li>
                <li><strong className="text-white">Restriction:</strong> Request limitation of processing</li>
                <li><strong className="text-white">Objection:</strong> Object to certain types of processing</li>
                <li><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                To exercise these rights, please contact us at privacy@watchthesize.com.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">8. Cookies and Tracking</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Maintain your session and authentication</li>
                <li>Remember your preferences</li>
                <li>Analyze usage patterns and improve our service</li>
                <li>Prevent fraud and abuse</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                You can control cookies through your browser settings. Disabling cookies may affect the functionality of our service.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">9. International Data Transfers</h2>
              <p className="text-zinc-400 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission, to protect your data during such transfers.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">10. Children's Privacy</h2>
              <p className="text-zinc-400 leading-relaxed">
                Our Service is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal data, please contact us.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
              <p className="text-zinc-400 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. For material changes, we will also send an email notification.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
              <p className="text-zinc-400 leading-relaxed">
                If you have questions or concerns about this privacy policy or our data practices, please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p className="text-violet-400">privacy@watchthesize.com</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm">
                📊
              </div>
              <span className="text-sm font-semibold text-white">WatchTheSize</span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="text-white">Privacy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
            
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} WatchTheSize. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

